// frontend/src/components/Auth/OnboardingModal.tsx
import { useState, useEffect, useRef, useCallback } from 'react';
import {
  GraduationCap,
  Calendar,
  Award,
  Loader,
  ChevronRight,
  X,
} from 'lucide-react';
import { typedApi } from '../../services/typedApiClient';
import { useApp } from '../../contexts/AppContext';
import { useInstitutionConfig } from '../../contexts/InstitutionConfigContext';
import { useEscapeKey } from '../../hooks/useEscapeKey';
import { dispatchDegreeAuditRefresh } from '../../utils/appEvents';

export const ONBOARDING_MODE = {
  NONE: null,
  FULL: 'full',
  MAJOR_ONLY: 'majorOnly',
  SEMESTER_ONLY: 'semesterOnly',
} as const;

export type OnboardingMode =
  | typeof ONBOARDING_MODE.NONE
  | typeof ONBOARDING_MODE.FULL
  | typeof ONBOARDING_MODE.MAJOR_ONLY
  | typeof ONBOARDING_MODE.SEMESTER_ONLY;

export interface OnboardingModalProps {
  isOpen?: boolean;
  onClose?: () => void;
  onComplete?: () => Promise<void> | void;
  /** Open the AP credits modal after onboarding finishes (UC Merced QA:
   *  an external tester never discovered AP credit support — the natural
   *  moment to offer it is setup, not a card waiting to be noticed). */
  onRequestAPCredits?: () => void;
  mode?: OnboardingMode;
}

interface ProgramOption {
  id: number;
  name: string;
  program_name: string;
  program_code: string;
  track_name: string | null;
  degree_type: string;
}

interface SemesterOption {
  value: string;
  label: string;
}

/**
 * Degree types that are NOT answers to "What's your major?".
 *
 * Deliberately an exclusion list. This filter used to be an ALLOW list of
 * `BS | BA | Exploratory`, which silently dropped every other bachelor's
 * degree: 7 BFA + 4 BM at SJSU, 4 BM + 2 BME + 1 BLA at UMD. A SJSU BFA
 * student could not select their own major during onboarding — though they
 * could add it immediately afterwards from the program modal, which has
 * always classified by exclusion.
 *
 * The backend agrees: `determine_display_type` (routes/academic_plans/
 * helpers.py) gives Minor/GE/Citation/Exploratory/UR/ADVISING their own type
 * and falls everything else through to 'major'. An allow list makes the next
 * institution's unfamiliar degree type (BSN, BArch, ...) vanish with no error;
 * an exclusion list makes it show up.
 *
 * Certificate is excluded here even though the shared classifier calls it a
 * major — a certificate is not what this question is asking, and UMD has 28
 * of them. That is a pre-existing disagreement between this modal and the
 * program modal; this narrows it rather than widening it.
 */
const NON_MAJOR_DEGREE_TYPES = [
  'Minor',
  'Citation',
  'Certificate',
  'GE',
  'UR',
  'ADVISING',
];

const OnboardingModal = ({
  isOpen,
  onClose,
  onComplete,
  onRequestAPCredits,
  mode = ONBOARDING_MODE.FULL,
}: OnboardingModalProps = {}) => {
  const { dbUser, refreshUser } = useApp();
  const { config: institutionConfig } = useInstitutionConfig();
  const [programs, setPrograms] = useState<ProgramOption[]>([]);
  const [semesterOptions, setSemesterOptions] = useState<SemesterOption[]>([]);
  const [selectedMajor, setSelectedMajor] = useState('');
  const [startSemester, setStartSemester] = useState('');
  const [loading, setLoading] = useState(false);
  const [programsLoading, setProgramsLoading] = useState(true);
  const [semestersLoading, setSemestersLoading] = useState(true);
  const [isAnimating, setIsAnimating] = useState(false);
  const [hasApCredits, setHasApCredits] = useState(false);

  // The "not sure yet?" hint names a real undeclared-major program, so it can
  // only be shown where one exists. UMD has exactly one (Letters and Sciences
  // - Exploratory); UC Merced and SJSU have none, and hardcoding UMD's name
  // told their students to pick an option that was never in their dropdown.
  // Derived from the fetched list rather than institution config so the hint
  // and the <option> it points at can never disagree.
  const exploratoryOption = programs.find((p) => p.degree_type === 'Exploratory');

  const showMajor = mode === ONBOARDING_MODE.FULL || mode === ONBOARDING_MODE.MAJOR_ONLY;
  const showStartSemester = mode === ONBOARDING_MODE.FULL || mode === ONBOARDING_MODE.SEMESTER_ONLY;
  // First-run setup only, and only where the institution has an AP policy.
  const showApQuestion =
    mode === ONBOARDING_MODE.FULL &&
    institutionConfig.supportsApCredits &&
    !!onRequestAPCredits;

  useEffect(() => {
    if (isOpen) {
      setIsAnimating(true);
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && dbUser) {
      if (showMajor) {
        fetchPrograms();
      } else {
        setProgramsLoading(false);
      }
      if (showStartSemester) {
        fetchSemesters();
      } else {
        setSemestersLoading(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, dbUser, showMajor, showStartSemester]);

  const fetchSemesters = async () => {
    setSemestersLoading(true);
    try {
      const response = await typedApi.academicPlans.getAvailableStartSemesters();
      setSemesterOptions(response.semesters || []);
    } catch (err) {
      console.error('[OnboardingModal] Failed to fetch semesters:', err);
      setSemesterOptions([]);
    } finally {
      setSemestersLoading(false);
    }
  };

  const fetchPrograms = async () => {
    setProgramsLoading(true);
    try {
      const response = await typedApi.programs.getHierarchical();
      const allPrograms: ProgramOption[] = [];
      (response?.programs || []).forEach((group) => {
        (group.children || []).forEach((program) => {
          // `selectable === false` marks auto-managed programs (Gen Ed,
          // University Requirements) that the backend already refuses to let
          // a user pick; the degree-type list covers the rest.
          const isSelectableMajor =
            program.selectable !== false &&
            !NON_MAJOR_DEGREE_TYPES.includes(program.degree_type ?? '');

          if (isSelectableMajor) {
            allPrograms.push({
              id: program.id ?? 0,
              name: `${program.program_name ?? ''}${
                program.track_name && program.track_name !== 'General Track'
                  ? ` - ${program.track_name}`
                  : ''
              }`,
              program_name: program.program_name ?? '',
              program_code: program.program_code ?? '',
              track_name: program.track_name ?? null,
              degree_type: program.degree_type ?? '',
            });
          }
        });
      });
      allPrograms.sort((a, b) => a.name.localeCompare(b.name));
      setPrograms(allPrograms);
    } catch (err) {
      console.error('Failed to fetch programs:', err);
    } finally {
      setProgramsLoading(false);
    }
  };

  const handleClose = () => {
    setIsAnimating(false);
    setTimeout(() => {
      if (onClose) onClose();
    }, 200);
  };

  const handleSkip = () => {
    handleClose();
    if (onComplete) onComplete();
  };

  useEscapeKey(() => {
    if (!loading) handleSkip();
  }, !!isOpen);

  const handleSubmit = async () => {
    setLoading(true);

    try {
      let planId: number | null = null;
      if (dbUser?.supabase_user_id) {
        const plansResponse = await typedApi.academicPlans.list();
        if (plansResponse.plans && plansResponse.plans.length > 0) {
          planId = plansResponse.plans[0].id ?? null;
        }
      }

      if (startSemester && planId) {
        await typedApi.academicPlans.reinitializeSemesters(planId, startSemester);
      }

      const program = selectedMajor
        ? programs.find((p) => p.id === parseInt(selectedMajor, 10))
        : null;

      if (program && planId) {
        await typedApi.academicPlans.updatePrograms(planId, {
          majors: [
            { program: program.program_name, track: program.track_name ?? '' },
          ],
          minors: [],
        });
      }

      // The audit hook keys off planId and won't refetch on its own when the
      // same plan's programs/semesters change underneath it. Without this,
      // the audit column renders the empty pre-onboarding state until reload.
      if (planId && (startSemester || program)) {
        dispatchDegreeAuditRefresh(planId);
      }

      if (refreshUser) {
        await refreshUser();
      }

      handleClose();
      if (onComplete) onComplete();
      // After setup: route straight into adding scores. Deliberately not on
      // the skip path — skipping means "not now" for everything.
      if (showApQuestion && hasApCredits) {
        onRequestAPCredits();
      }
    } catch (err) {
      console.error('Onboarding error:', err);
      handleClose();
      if (onComplete) onComplete();
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div
        className={`fixed inset-0 bg-black transition-opacity duration-200 ${
          isAnimating ? 'bg-opacity-50' : 'bg-opacity-0'
        }`}
        onClick={() => {
          if (!loading) handleSkip();
        }}
      />

      <div className="flex min-h-full items-center justify-center p-4">
        <div
          className={`relative bg-white rounded-xl shadow-xl max-w-md w-full transform transition-all duration-200 ${
            isAnimating ? 'scale-100 opacity-100' : 'scale-95 opacity-0'
          }`}
        >
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <GraduationCap className="h-5 w-5 text-blue-600" />
                </div>
                <div className="ml-3">
                  <h2 className="text-lg font-semibold text-gray-900">
                    Welcome to My Academic Planner!
                  </h2>
                  <p className="text-sm text-gray-500">
                    Let's set up your plan
                  </p>
                </div>
              </div>
              <button
                onClick={handleSkip}
                disabled={loading}
                className="p-1 text-gray-400 hover:text-gray-600 rounded-md disabled:opacity-50"
                aria-label="Close"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>

          <div className="px-6 py-6 space-y-6">
            <p className="text-sm text-gray-600">
              {mode === ONBOARDING_MODE.FULL
                ? 'Help us personalize your experience. Both fields are optional - you can always update them later.'
                : mode === ONBOARDING_MODE.MAJOR_ONLY
                  ? 'Help us personalize your experience by selecting your major. This is optional - you can always update it later.'
                  : 'Help us set up your academic timeline. This is optional - you can always update it later.'}
            </p>

            {showMajor && (
              <div>
                <label htmlFor="major" className="block text-sm font-medium text-gray-700 mb-1">
                  What's your major?
                </label>
                {programsLoading ? (
                  <div className="flex items-center justify-center py-3">
                    <Loader className="h-5 w-5 animate-spin text-gray-400" />
                    <span className="ml-2 text-sm text-gray-500">Loading programs...</span>
                  </div>
                ) : (
                  <select
                    id="major"
                    value={selectedMajor}
                    onChange={(e) => setSelectedMajor(e.target.value)}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  >
                    <option value="">Select a major (optional)</option>
                    {programs.map((program) => (
                      <option key={program.id} value={program.id}>
                        {program.name}
                      </option>
                    ))}
                  </select>
                )}
                {exploratoryOption && (
                  <p className="mt-1 text-xs text-gray-500">
                    Not sure yet? Select{' '}
                    <span className="font-medium">{exploratoryOption.name}</span>
                    {' '}for an undeclared major.
                  </p>
                )}
              </div>
            )}

            {showApQuestion && (
              <div>
                <label className="flex items-start gap-2.5 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={hasApCredits}
                    onChange={(e) => setHasApCredits(e.target.checked)}
                    className="mt-0.5 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span>
                    <span className="block text-sm font-medium text-gray-700">
                      <Award className="h-4 w-4 inline mr-1" />
                      I have AP exam credit
                    </span>
                    <span className="block mt-0.5 text-xs text-gray-500">
                      AP scores can satisfy degree requirements. We'll open the
                      AP credits panel after setup so you can add yours.
                    </span>
                  </span>
                </label>
              </div>
            )}

            {showStartSemester && (
              <div>
                <label htmlFor="startSemester" className="block text-sm font-medium text-gray-700 mb-1">
                  <Calendar className="h-4 w-4 inline mr-1" />
                  When did/will you start?
                </label>
                {semestersLoading ? (
                  <div className="flex items-center justify-center py-3">
                    <Loader className="h-5 w-5 animate-spin text-gray-400" />
                    <span className="ml-2 text-sm text-gray-500">Loading semesters...</span>
                  </div>
                ) : (
                  <select
                    id="startSemester"
                    value={startSemester}
                    onChange={(e) => setStartSemester(e.target.value)}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  >
                    <option value="">Select start semester (optional)</option>
                    {semesterOptions.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                )}
                <p className="mt-1 text-xs text-gray-500">
                  We'll create a timeline based on your start date
                </p>
              </div>
            )}
          </div>

          <div className="px-6 py-4 border-t border-gray-200 flex justify-between">
            <button
              onClick={handleSkip}
              disabled={loading}
              className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-900 disabled:opacity-50"
            >
              Skip for now
            </button>
            <button
              onClick={handleSubmit}
              disabled={loading}
              className="flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
            >
              {loading ? (
                <>
                  <Loader className="h-4 w-4 animate-spin mr-2" />
                  Setting up...
                </>
              ) : (
                <>
                  Get Started
                  <ChevronRight className="h-4 w-4 ml-1" />
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const DISMISSED_KEY_PREFIX = 'onboarding_dismissed_';

export interface UseOnboardingResult {
  showOnboarding: boolean;
  setShowOnboarding: React.Dispatch<React.SetStateAction<boolean>>;
  onboardingMode: OnboardingMode;
  completeOnboarding: () => Promise<void>;
  dismissOnboarding: () => void;
}

export const useOnboarding = (): UseOnboardingResult => {
  const { dbUser } = useApp();
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [onboardingMode, setOnboardingMode] = useState<OnboardingMode>(ONBOARDING_MODE.NONE);
  const hasEvaluatedRef = useRef(false);

  useEffect(() => {
    if (hasEvaluatedRef.current) return;

    if (!dbUser?.preferences) return;

    hasEvaluatedRef.current = true;

    if (sessionStorage.getItem('suppress_onboarding')) {
      sessionStorage.removeItem('suppress_onboarding');
      return;
    }

    const dismissedKey = DISMISSED_KEY_PREFIX + (dbUser.supabase_user_id || dbUser.id);
    if (sessionStorage.getItem(dismissedKey)) {
      return;
    }

    const prefs = dbUser.preferences;
    const hasMajor = prefs.has_majors_in_plan ?? true;
    const hasSemesters = prefs.has_semesters_in_plan ?? true;

    let mode: OnboardingMode = ONBOARDING_MODE.NONE;
    if (!hasMajor && !hasSemesters) {
      mode = ONBOARDING_MODE.FULL;
    } else if (!hasMajor) {
      mode = ONBOARDING_MODE.MAJOR_ONLY;
    } else if (!hasSemesters) {
      mode = ONBOARDING_MODE.SEMESTER_ONLY;
    }

    if (mode !== ONBOARDING_MODE.NONE) {
      setOnboardingMode(mode);
      setShowOnboarding(true);
    }
  }, [dbUser]);

  const completeOnboarding = useCallback(async () => {
    if (dbUser) {
      const dismissedKey = DISMISSED_KEY_PREFIX + (dbUser.supabase_user_id || dbUser.id);
      sessionStorage.setItem(dismissedKey, 'true');
    }
    setShowOnboarding(false);
    setOnboardingMode(ONBOARDING_MODE.NONE);
  }, [dbUser]);

  const dismissOnboarding = useCallback(() => {
    if (dbUser) {
      const dismissedKey = DISMISSED_KEY_PREFIX + (dbUser.supabase_user_id || dbUser.id);
      sessionStorage.setItem(dismissedKey, 'true');
    }
    setShowOnboarding(false);
    setOnboardingMode(ONBOARDING_MODE.NONE);
  }, [dbUser]);

  return { showOnboarding, setShowOnboarding, onboardingMode, completeOnboarding, dismissOnboarding };
};

export default OnboardingModal;
